import React, { useEffect, useState } from 'react';
import axios from 'axios';
import PageHeader from '../../../shared/page-header/PageHeader';
import SKAlerts, { CreateAlert } from '../../../shared/common-alerts/SKAlerts';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { getModulePermissions } from '../../../utils/permissionHelper';
import { logAuditEvent } from '../../../utils/auditLogger';
import { getStorageValues } from '../../../shared/CommonFunctions';
import { exportToExcel, exportToPDF } from '../../../utils/downloadExportedFiles';
import ProductCategoryGrid from './ProductCategoryGrid';
import ProductCategoryModal from './ProductCategoryModal';
import { buildProductCategorySavePayload } from './utils/mapProductCategory';
import { validateProductCategory } from './productCategoryValidation';

function ProductCategoryMain(props) {
  const [categoryList, setCategoryList] = useState([]);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [userAction, setUserAction] = useState('Add');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [alert, setAlert] = useState(null);
  const [userModulePermission, setUserModulePermission] = useState({});
  const moduleName = props.Module || 'Product Category';

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${getApiBaseUrl()}/ec-product-category/getAll`);
      if (response.status === 200) {
        setCategoryList(response.data?.data || []);
      }
    } catch (error) {
      console.error(error);
      CreateAlert('Failed to load product categories!!', 'Error', setAlert);
    }
  };

  useEffect(() => {
    const modulePermissions = getModulePermissions(props.Module);
    setUserModulePermission(modulePermissions ? modulePermissions : []);
    fetchCategories();
    logAuditEvent(moduleName, 'Accessed Module', 'Accessed Product Category module');
  }, [props.Module, moduleName]);

  const openAddModal = () => {
    setUserAction('Add');
    setSelectedCategory(null);
    setIsOpen(true);
    logAuditEvent(moduleName, 'Add Button Click', 'Opened add product category modal');
  };

  const closeModal = () => {
    setIsOpen(false);
    setSelectedCategory(null);
    setUserAction('Add');
  };

  const onSubmit = async (e, data) => {
    if (e.target.name !== 'Add' && e.target.name !== 'Edit') return;

    const payload = buildProductCategorySavePayload(data, getStorageValues('userId'));
    if (!validateProductCategory(payload, setAlert)) return;

    try {
      const response = await axios.post(`${getApiBaseUrl()}/ec-product-category/save`, payload);
      const result = response.data?.data;
      if (result?.success === 1) {
        const isAdd = e.target.name === 'Add';
        CreateAlert(
          result.message || (isAdd ? 'Category added successfully!!' : 'Category updated successfully!!'),
          'Success',
          setAlert
        );
        await fetchCategories();
        if (isAdd) {
          closeModal();
        }
      } else {
        CreateAlert(result?.message || 'Failed to save category!!', 'Error', setAlert);
      }
    } catch (error) {
      console.error(error);
      CreateAlert('Failed to save category!!', 'Error', setAlert);
    }
  };

  const handleExportExcel = () => {
    const columns = [
      { name: 'Category Name', selector: 'CategoryName' },
      { name: 'Slug', selector: 'Slug' },
      { name: 'Image URL', selector: 'ImageUrl' },
      { name: 'Display Order', selector: 'DisplayOrder' },
      { name: 'Active', selector: 'IsActive' },
    ];
    exportToExcel('Product Categories', columns, categoryList);
  };

  const handleExportPDF = () => {
    const columns = [
      { name: 'Category Name', selector: 'CategoryName' },
      { name: 'Slug', selector: 'Slug' },
      { name: 'Image URL', selector: 'ImageUrl' },
      { name: 'Display Order', selector: 'DisplayOrder' },
      { name: 'Active', selector: 'IsActive' },
    ];
    exportToPDF('Product Categories', columns, categoryList);
  };

  return (
    <>
      <SKAlerts alert={alert} />
      <PageHeader
        displayModule={props.Module}
        onModelOpen={openAddModal}
        onRefresh={fetchCategories}
        showPDF={true}
        showExcel={true}
        showPrint={false}
        showRefresh={true}
        onExcelExport={handleExportExcel}
        onPdfExport={handleExportPDF}
        showAddButton={userModulePermission.AddModule === 1}
      />
      <ProductCategoryGrid
        CategoryData={categoryList}
        fetchCategories={fetchCategories}
        UserPermissions={userModulePermission}
        moduleName={moduleName}
      />
      <ProductCategoryModal
        show={modalIsOpen}
        UserAction={userAction}
        selectedRow={selectedCategory}
        onClose={closeModal}
        onSubmit={onSubmit}
        moduleName={moduleName}
      />
    </>
  );
}

export default ProductCategoryMain;
